import CheckmarkIcon from '@assets/icons/checkmark.svg';
import React, { useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { Image, Platform, Text, View } from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';

interface TagPrintingTemplateImageProps {
  isSelected: boolean;
  previewImageUrlList: string[] | null;
  templateName: string;
}

export const TagPrintingTemplateImage: React.FC<
  TagPrintingTemplateImageProps
> = ({ isSelected, previewImageUrlList, templateName }) => {
  const { isSmallLayout } = useLayoutContext();
  const [styles, theme] = useStyles();
  const [hasError, setHasError] = useState(false);
  const imageUrl = previewImageUrlList?.[0];

  const templateImage = (
    <Image
      alt="Generated tag template image"
      onError={() => {
        setHasError(true);
      }}
      resizeMethod="auto"
      resizeMode="contain"
      source={{
        uri: imageUrl,
      }}
      style={styles.templateImage}
      testID={`tag-printing-template-image-${imageUrl}`}
    />
  );

  const selectedOverlay = (
    <>
      <View
        style={styles.corner}
        testID={`tag-template-selected-${templateName}`}
      />
      <CheckmarkIcon
        height={25}
        stroke={
          Platform.OS === 'android' ? theme.colors.accent : theme.colors.white
        }
        style={styles.checkmarkIcon}
        width={25}
      />
    </>
  );

  const templateImageFallback = (
    <View style={styles.templateImage}>
      <Text>{templateName}</Text>
    </View>
  );

  return (
    <>
      {imageUrl && !hasError ? templateImage : templateImageFallback}
      {isSelected ? selectedOverlay : null}
      <Text
        numberOfLines={1}
        style={[
          TYPOGRAPHY.medium,
          FONTS.primarySemiBold,
          styles.templateText,
          isSmallLayout
            ? styles.templateNamePaddingVerticalSmall
            : styles.templateNamePaddingVertical,
        ]}
      >
        {templateName}
      </Text>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  checkmarkIcon: {
    position: 'absolute',
    right: 5,
    top: 5,
  },
  corner: {
    borderLeftColor: theme.colors.transparent,
    borderLeftWidth: 60,
    borderTopColor: theme.colors.accent,
    borderTopRightRadius: 12,
    borderTopWidth: 60,
    height: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    width: 0,
  },
  templateImage: {
    alignItems: 'center',
    backgroundColor: theme.colors.divider,
    borderTopLeftRadius: 14,
    borderTopRightRadius: 14,
    flex: 1,
    justifyContent: 'center',
  },
  templateNamePaddingVertical: {
    paddingVertical: 12,
  },
  templateNamePaddingVerticalSmall: {
    paddingVertical: 6,
  },
  templateText: {
    justifyContent: 'center',
    lineHeight: 20,
    paddingHorizontal: 12,
  },
}));
